/**
 * Governance Finance - Unified Page
 * Single page with tabs: Receita, Premissas, DRE, Fluxo de Caixa, Despesas, FinOps, Vukz Fin
 */
import React, { useState, useEffect } from 'react';
import {
  DollarSign, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight,
  Plus, MessageSquare, BarChart3, Wallet, PieChart, Send, Bot, RefreshCw,
  Users, Building2, Calendar, Filter, ChevronDown, ChevronRight, Settings
} from 'lucide-react';
import { AZURE } from '../../services/api';
import { COLORS } from '../../components/governance/governanceTheme';
import AzureFinOpsTab from '../../components/governance/AzureFinOpsTab';
import AzureFXCreditPanel from '../../components/governance/AzureFXCreditPanel';
import AzureSubscriptionsPanel from '../../components/governance/AzureSubscriptionsPanel';
import FinancialPlanningTab from '../../components/governance/FinancialPlanningTab';
import FinancialExpensesTable from '../../components/governance/FinancialExpensesTable';
import FinancialPremisesTab from '../../components/governance/FinancialPremisesTab';
import FinancialDREDetailedTab from '../../components/governance/FinancialDREDetailedTab';
import FinancialCashflowDetailedTab from '../../components/governance/FinancialCashflowDetailedTab';
import ProductProjectionsTab from '../../components/governance/ProductProjectionsTab';
import CostCentersTab from '../../components/governance/CostCentersTab';
import MarketingCampaignExpenses from '../../components/governance/MarketingCampaignExpenses';
import FinancialScenariosPage from './commercial/FinancialScenariosPage';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

const BRAND_GREEN = '#064e3b';
const BRAND_GOLD = '#b48c2c';

const FINANCE_LABELS = {
  pt: {
    title: 'Financeiro',
    subtitle: 'Receita, despesas, DRE, fluxo de caixa e FinOps',
    tabs: [
      { id: 'receita', label: 'Receita', icon: TrendingUp },
      { id: 'despesas', label: 'Despesas', icon: TrendingDown },
      { id: 'dre', label: 'DRE', icon: BarChart3 },
      { id: 'fluxo', label: 'Fluxo de Caixa', icon: Wallet },
      { id: 'produtos', label: 'Produtos', icon: PieChart },
      { id: 'centros', label: 'Centros de Custos', icon: Building2 },
      { id: 'premissas', label: 'Premissas', icon: Settings },
      { id: 'cenarios', label: '9 Cenários', icon: Filter },
      { id: 'finops', label: 'FinOps', icon: PieChart },
      { id: 'chat', label: 'Vukz Fin', icon: MessageSquare },
    ],
    kpiMrr: 'MRR Atual', kpiRecurring: 'Receita Recorrente',
    kpiOneTime: 'Receita Pontual', kpiClients: 'Clientes Ativos',
    kpiAccum: 'Total acumulado', kpiSetupNote: 'Setup fees, consultorias',
    kpiPayingThis: 'Pagantes este mês',
    viewByClient: 'Por Cliente', viewBySource: 'Por Fonte', viewByMonth: 'Por Mês',
    addRevenue: 'Nova Receita', formTitle: 'Registrar Receita',
    formSave: 'Salvar', formRecurring: 'Recorrente (MRR)',
    clientSection: 'Receita por Cliente',
    clientCount: (n) => `${n} clientes`,
    thClient: 'Cliente', thSources: 'Fontes', thMrr: 'MRR', thTotal: 'Total',
    noRevenue: 'Nenhuma receita registrada. Clientes com assinaturas ativas aparecerão automaticamente.',
    totalLabel: 'TOTAL',
    launches: (n) => `lançamento${n > 1 ? 's' : ''}`,
    sourceSection: 'Receita por Fonte', noRevenueSrc: 'Nenhuma receita registrada.',
    monthSection: 'Receita por Mês',
    thMonth: 'Mês', thRecurring: 'Recorrente', thOneTime: 'Pontual', thCount: '#',
    noRevenueMonth: 'Nenhuma receita registrada.',
    activeSubsSection: 'Assinaturas Ativas (billing)',
    subCount: (n) => `${n} assinaturas`,
    thPlan: 'Plano', thStatus: 'Status', thMonthly: 'Mensal', thStart: 'Início',
    activeLabel: 'Ativo',
    chatSubtitle: (n) => `CFO Agent • ${n} mensagens no historico`,
    chatClear: 'Limpar',
    chatPlaceholder: 'Ex: Adiciona despesa de R$500 com Azure OpenAI...',
    chatThinking: 'Pensando...',
  },
  en: {
    title: 'Financial',
    subtitle: 'Revenue, expenses, P&L, cash flow and FinOps',
    tabs: [
      { id: 'receita', label: 'Revenue', icon: TrendingUp },
      { id: 'despesas', label: 'Expenses', icon: TrendingDown },
      { id: 'dre', label: 'P&L', icon: BarChart3 },
      { id: 'fluxo', label: 'Cash Flow', icon: Wallet },
      { id: 'produtos', label: 'Products', icon: PieChart },
      { id: 'centros', label: 'Cost Centers', icon: Building2 },
      { id: 'premissas', label: 'Assumptions', icon: Settings },
      { id: 'cenarios', label: '9 Scenarios', icon: Filter },
      { id: 'finops', label: 'FinOps', icon: PieChart },
      { id: 'chat', label: 'Vukz Fin', icon: MessageSquare },
    ],
    kpiMrr: 'Current MRR', kpiRecurring: 'Recurring Revenue',
    kpiOneTime: 'One-time Revenue', kpiClients: 'Active Clients',
    kpiAccum: 'Total accumulated', kpiSetupNote: 'Setup fees, consulting',
    kpiPayingThis: 'Paying this month',
    viewByClient: 'By Client', viewBySource: 'By Source', viewByMonth: 'By Month',
    addRevenue: 'New Revenue', formTitle: 'Register Revenue',
    formSave: 'Save', formRecurring: 'Recurring (MRR)',
    clientSection: 'Revenue by Client',
    clientCount: (n) => `${n} clients`,
    thClient: 'Client', thSources: 'Sources', thMrr: 'MRR', thTotal: 'Total',
    noRevenue: 'No revenue registered. Clients with active subscriptions will appear automatically.',
    totalLabel: 'TOTAL',
    launches: (n) => `launch${n > 1 ? 'es' : ''}`,
    sourceSection: 'Revenue by Source', noRevenueSrc: 'No revenue registered.',
    monthSection: 'Revenue by Month',
    thMonth: 'Month', thRecurring: 'Recurring', thOneTime: 'One-time', thCount: '#',
    noRevenueMonth: 'No revenue registered.',
    activeSubsSection: 'Active Subscriptions (billing)',
    subCount: (n) => `${n} subscriptions`,
    thPlan: 'Plan', thStatus: 'Status', thMonthly: 'Monthly', thStart: 'Start',
    activeLabel: 'Active',
    chatSubtitle: (n) => `CFO Agent • ${n} messages in history`,
    chatClear: 'Clear',
    chatPlaceholder: 'Ex: Add R$500 expense for Azure OpenAI...',
    chatThinking: 'Thinking...',
  },
};

// ============ RECEITA TAB (Detailed) ============
const ReceitaTab = ({ t }) => {
  const [revenues, setRevenues] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ month: '', source: 'subscription', amount: '', description: '', is_recurring: false, client_name: '' });
  const [viewMode, setViewMode] = useState('by_client'); // 'by_client', 'by_source', 'by_month', 'timeline'
  const [showForm, setShowForm] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [revRes, subRes] = await Promise.allSettled([
        AZURE.get('/governance/finance/planning/revenues'),
        AZURE.get('/billing/subscriptions'),
      ]);
      const revs = revRes.status === 'fulfilled' ? revRes.value?.data?.data || [] : [];
      const subs = subRes.status === 'fulfilled' ? subRes.value?.data?.data || subRes.value?.data || [] : [];
      setRevenues(revs);
      setSubscriptions(subs);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await AZURE.post('/governance/finance/planning/revenues', {
        ...form, amount: parseFloat(form.amount), is_recurring: form.is_recurring ? 1 : 0
      });
      setForm({ month: '', source: 'subscription', amount: '', description: '', is_recurring: false, client_name: '' });
      setShowForm(false);
      loadData();
    } catch (err) { console.error(err); }
  };

  // Combine revenue sources: real revenues + active subscriptions (as MRR)
  const allRevenues = [...revenues];

  // Gate 1: mês corrente em America/Sao_Paulo (não UTC — evita virada errada 21h–00h BRT)
  const currentMonth = (() => {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit',
    }).formatToParts(new Date());
    return `${parts.find(p => p.type === 'year').value}-${parts.find(p => p.type === 'month').value}`;
  })();
  subscriptions.filter(s => s.status === 'active').forEach(sub => {
    const alreadyExists = revenues.some(r => 
      r.month === currentMonth && r.client_institution_id === sub.institution_id && r.source === 'subscription'
    );
    if (!alreadyExists && sub.monthly_price) {
      allRevenues.push({
        month: currentMonth,
        source: 'subscription',
        amount: sub.monthly_price_cents ? sub.monthly_price_cents / 100 : sub.monthly_price || 0,
        description: `${sub.plan_name || 'Assinatura'} (projeção — não realizado)`,
        client_name: sub.institution_name || sub.club_name || `Institution #${sub.institution_id || sub.club_id}`,
        client_institution_id: sub.institution_id || sub.club_id,
        is_recurring: 1,
        is_from_billing: true,
      });
    }
  });

  // KPIs — Gate 1: MRR sintético (is_from_billing) é PROJEÇÃO, nunca receita realizada.
  // Realizado = somente lançamentos reais de financial_revenues.
  const realizedRevenues = allRevenues.filter(r => !r.is_from_billing);
  const projectedMRR = allRevenues.filter(r => r.is_from_billing && r.month === currentMonth)
    .reduce((s, r) => s + (r.amount || 0), 0);
  const totalMRR = realizedRevenues.filter(r => r.month === currentMonth).reduce((s, r) => s + (r.amount || 0), 0);
  const recurringRevenue = realizedRevenues.filter(r => r.is_recurring).reduce((s, r) => s + (r.amount || 0), 0);
  const oneTimeRevenue = realizedRevenues.filter(r => !r.is_recurring).reduce((s, r) => s + (r.amount || 0), 0);
  const activeClients = new Set(allRevenues.filter(r => r.month === currentMonth).map(r => r.client_institution_id || r.client_name || r.description)).size;

  // Group by different dimensions
  const groupByClient = () => {
    const map = {};
    allRevenues.forEach(r => {
      const key = r.client_name || r.description || 'Não identificado';
      if (!map[key]) map[key] = { name: key, total: 0, mrr: 0, items: [], sources: new Set() };
      map[key].total += r.amount || 0;
      if (r.is_recurring || r.source === 'subscription') map[key].mrr += r.amount || 0;
      map[key].items.push(r);
      map[key].sources.add(r.source);
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  };

  const groupBySource = () => {
    const map = {};
    allRevenues.forEach(r => {
      const key = r.source || 'other';
      if (!map[key]) map[key] = { source: key, total: 0, count: 0, items: [] };
      map[key].total += r.amount || 0;
      map[key].count++;
      map[key].items.push(r);
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  };

  const groupByMonth = () => {
    const map = {};
    allRevenues.forEach(r => {
      const key = r.month || 'unknown';
      if (!map[key]) map[key] = { month: key, total: 0, recurring: 0, oneTime: 0, count: 0 };
      map[key].total += r.amount || 0;
      map[key].count++;
      if (r.is_recurring || r.source === 'subscription') map[key].recurring += r.amount || 0;
      else map[key].oneTime += r.amount || 0;
    });
    return Object.entries(map).sort(([a], [b]) => b.localeCompare(a)).map(([, v]) => v);
  };

  const SOURCE_LABELS = {
    subscription: 'Assinatura',
    setup_fee: 'Setup Fee',
    addon: 'Addon',
    enterprise: 'Enterprise',
    partnership: 'Parceria',
    consulting: 'Consultoria',
    other: 'Outro',
  };

  const SOURCE_COLORS = {
    subscription: { bg: '#d1fae5', text: '#065f46' },
    setup_fee: { bg: '#dbeafe', text: '#1e40af' },
    addon: { bg: '#fef3c7', text: '#92400e' },
    enterprise: { bg: '#ede9fe', text: '#5b21b6' },
    partnership: { bg: '#fce7f3', text: '#9d174d' },
    consulting: { bg: '#e0f2fe', text: '#0c4a6e' },
    other: { bg: '#f3f4f6', text: '#374151' },
  };

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 bg-white rounded-xl animate-pulse" />)}</div>;

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-500 uppercase font-semibold">{t.kpiMrr}</div>
          <div className="text-2xl font-bold" style={{ color: BRAND_GREEN }}>
            R${totalMRR.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            {currentMonth}
            {projectedMRR > 0 && (
              <span className="ml-1 text-amber-600 font-semibold">
                + R${projectedMRR.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} projeção
              </span>
            )}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-500 uppercase font-semibold">{t.kpiRecurring}</div>
          <div className="text-2xl font-bold text-green-700">
            R${recurringRevenue.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
          </div>
          <div className="text-xs text-gray-400 mt-1">{t.kpiAccum}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-500 uppercase font-semibold">{t.kpiOneTime}</div>
          <div className="text-2xl font-bold" style={{ color: BRAND_GOLD }}>
            R${oneTimeRevenue.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
          </div>
          <div className="text-xs text-gray-400 mt-1">{t.kpiSetupNote}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-500 uppercase font-semibold">{t.kpiClients}</div>
          <div className="text-2xl font-bold text-indigo-700">{activeClients}</div>
          <div className="text-xs text-gray-400 mt-1">{t.kpiPayingThis}</div>
        </div>
      </div>

      {/* Toolbar: Add + View toggle */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
          {[
            { id: 'by_client', label: t.viewByClient, icon: Users },
            { id: 'by_source', label: t.viewBySource, icon: Filter },
            { id: 'by_month', label: t.viewByMonth, icon: Calendar },
          ].map(v => (
            <button key={v.id} onClick={() => setViewMode(v.id)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition ${
                viewMode === v.id ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}>
              <v.icon size={12} />{v.label}
            </button>
          ))}
        </div>
        <button onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-white text-sm font-bold"
          style={{ backgroundColor: BRAND_GREEN }}>
          <Plus size={14} /> {t.addRevenue}
        </button>
      </div>

      {/* Add form (collapsible) */}
      {showForm && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="font-bold text-gray-900 mb-3 text-sm">{t.formTitle}</h3>
          <form onSubmit={handleAdd} className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
            <input type="month" value={form.month} onChange={e => setForm({...form, month: e.target.value})} className="border rounded-lg px-3 py-2 text-sm" required />
            <input placeholder="Cliente" value={form.client_name} onChange={e => setForm({...form, client_name: e.target.value})} className="border rounded-lg px-3 py-2 text-sm" />
            <select value={form.source} onChange={e => setForm({...form, source: e.target.value})} className="border rounded-lg px-3 py-2 text-sm">
              {Object.entries(SOURCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <input type="number" step="0.01" placeholder="R$" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} className="border rounded-lg px-3 py-2 text-sm" required />
            <input placeholder="Descrição" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="border rounded-lg px-3 py-2 text-sm" />
            <button type="submit" className="px-4 py-2 rounded-lg text-white font-bold text-sm" style={{ backgroundColor: BRAND_GREEN }}>{t.formSave}</button>
          </form>
          <label className="flex items-center gap-2 mt-2 text-xs text-gray-500">
            <input type="checkbox" checked={form.is_recurring} onChange={e => setForm({...form, is_recurring: e.target.checked})} /> {t.formRecurring}
          </label>
        </div>
      )}

      {/* View: By Client */}
      {viewMode === 'by_client' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b bg-green-50 flex items-center gap-2">
            <Users size={16} className="text-green-700" />
            <h4 className="font-bold text-green-800 text-sm">{t.clientSection}</h4>
            <span className="ml-auto text-xs text-gray-500">{t.clientCount(groupByClient().length)}</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600">{t.thClient}</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600">{t.thSources}</th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-600">{t.thMrr}</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-600">{t.thTotal}</th>
              </tr>
            </thead>
            <tbody>
              {groupByClient().map((client, i) => (
                <tr key={i} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                        <Building2 size={14} className="text-gray-500" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{client.name}</div>
                        <div className="text-xs text-gray-400">{client.items.length} {t.launches(client.items.length)}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-1">
                      {[...client.sources].map(s => {
                        const c = SOURCE_COLORS[s] || SOURCE_COLORS.other;
                        return <span key={s} className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: c.bg, color: c.text }}>
                          {SOURCE_LABELS[s] || s}
                        </span>;
                      })}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right font-mono text-sm text-green-700 font-semibold">
                    {client.mrr > 0 ? `R$${client.mrr.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-sm font-bold text-gray-900">
                    R${client.total.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                  </td>
                </tr>
              ))}
              {groupByClient().length === 0 && (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500 text-sm">
                  {t.noRevenue}
                </td></tr>
              )}
            </tbody>
            {groupByClient().length > 0 && (
              <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                <tr>
                  <td className="px-4 py-3 font-bold text-gray-900">{t.totalLabel}</td>
                  <td></td>
                  <td className="px-3 py-3 text-right font-mono font-bold text-green-700">
                    R${groupByClient().reduce((s, c) => s + c.mrr, 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-gray-900">
                    R${groupByClient().reduce((s, c) => s + c.total, 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {/* View: By Source */}
      {viewMode === 'by_source' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b bg-indigo-50 flex items-center gap-2">
            <Filter size={16} className="text-indigo-700" />
            <h4 className="font-bold text-indigo-800 text-sm">{t.sourceSection}</h4>
          </div>
          <div className="p-4 space-y-3">
            {groupBySource().map((src, i) => {
              const pct = totalMRR > 0 ? (src.total / allRevenues.reduce((s, r) => s + (r.amount || 0), 0) * 100) : 0;
              const c = SOURCE_COLORS[src.source] || SOURCE_COLORS.other;
              return (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs font-bold px-2 py-1 rounded-full min-w-[100px] text-center" style={{ backgroundColor: c.bg, color: c.text }}>
                    {SOURCE_LABELS[src.source] || src.source}
                  </span>
                  <div className="flex-1">
                    <div className="h-6 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: c.text + '30' }} />
                    </div>
                  </div>
                  <div className="text-right min-w-[120px]">
                    <div className="font-mono font-bold text-sm text-gray-900">R${src.total.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}</div>
                    <div className="text-[10px] text-gray-400">{src.count} lançamento{src.count > 1 ? 's' : ''} • {pct.toFixed(0)}%</div>
                  </div>
                </div>
              );
            })}
            {groupBySource().length === 0 && (
              <p className="text-center text-gray-500 text-sm py-4">{t.noRevenueSrc}</p>
            )}
          </div>
        </div>
      )}

      {/* View: By Month */}
      {viewMode === 'by_month' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b bg-blue-50 flex items-center gap-2">
            <Calendar size={16} className="text-blue-700" />
            <h4 className="font-bold text-blue-800 text-sm">{t.monthSection}</h4>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600">{t.thMonth}</th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-600">{t.thRecurring}</th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-600">{t.thOneTime}</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-600">{t.thTotal}</th>
                <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-600">{t.thCount}</th>
              </tr>
            </thead>
            <tbody>
              {groupByMonth().map((m, i) => (
                <tr key={i} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-medium text-gray-900">{m.month}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-green-700">
                    {m.recurring > 0 ? `R$${m.recurring.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}` : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono text-amber-700">
                    {m.oneTime > 0 ? `R$${m.oneTime.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}` : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono font-bold text-gray-900">
                    R${m.total.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                  </td>
                  <td className="px-3 py-2.5 text-center text-gray-400 text-xs">{m.count}</td>
                </tr>
              ))}
              {groupByMonth().length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">{t.noRevenueMonth}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Active Subscriptions (always visible) */}
      {subscriptions.filter(s => s.status === 'active').length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b bg-emerald-50 flex items-center gap-2">
            <RefreshCw size={14} className="text-emerald-700" />
            <h4 className="font-bold text-emerald-800 text-sm">{t.activeSubsSection}</h4>
            <span className="ml-auto text-xs text-emerald-600 font-semibold">
              {t.subCount(subscriptions.filter(s => s.status === 'active').length)}
            </span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">{t.thPlan}</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">{t.thStatus}</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">{t.thMonthly}</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">{t.thStart}</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.filter(s => s.status === 'active').map((sub, i) => (
                <tr key={i} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-medium text-gray-900">{sub.plan_name || 'Assinatura'}</td>
                  <td className="px-3 py-2.5">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-800">{t.activeLabel}</span>
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono font-semibold text-green-700">
                    R${((sub.monthly_price_cents ? sub.monthly_price_cents / 100 : sub.monthly_price) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-3 py-2.5 text-gray-500 text-xs">{sub.started_at?.slice(0, 10) || sub.start_date || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ============ CHAT TAB (Vukz Fin) ============
const VUKZ_FIN_STORAGE_KEY = 'vukz_fin_chat_history';
const VUKZ_FIN_CONTEXT_KEY = 'vukz_fin_context';
const VUKZ_FIN_INITIAL = { role: 'assistant', content: 'Olá! Sou o Vukz Fin, seu CFO virtual. Posso ajudar com:\n• Adicionar receitas e despesas\n• Alterar premissas do planejamento\n• Projetar cenários\n• Analisar DRE e fluxo de caixa\n\nComo posso ajudar?' };

const loadChatHistory = (key, initial) => {
  try {
    const saved = localStorage.getItem(key);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {}
  return [initial];
};

const loadContext = (key) => {
  try {
    const saved = localStorage.getItem(key);
    if (saved) return JSON.parse(saved);
  } catch {}
  return [];
};

const VukzFinChat = ({ t }) => {
  const [messages, setMessages] = useState(() => loadChatHistory(VUKZ_FIN_STORAGE_KEY, VUKZ_FIN_INITIAL));
  const [context] = useState(() => loadContext(VUKZ_FIN_CONTEXT_KEY));
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);

  // Persist messages to localStorage
  useEffect(() => {
    if (messages.length > 1) {
      localStorage.setItem(VUKZ_FIN_STORAGE_KEY, JSON.stringify(messages.slice(-100)));
    }
  }, [messages]);

  // Extract learnings from assistant messages and save as context
  useEffect(() => {
    const learnings = messages
      .filter(m => m.role === 'assistant' && m.content.length > 50)
      .slice(-20)
      .map(m => m.content.substring(0, 200));
    if (learnings.length > 0) {
      localStorage.setItem(VUKZ_FIN_CONTEXT_KEY, JSON.stringify(learnings));
    }
  }, [messages]);

  const clearHistory = () => {
    localStorage.removeItem(VUKZ_FIN_STORAGE_KEY);
    localStorage.removeItem(VUKZ_FIN_CONTEXT_KEY);
    setMessages([VUKZ_FIN_INITIAL]);
  };

  const handleSend = async () => {
    if (!input.trim()) return;
    const userMsg = { role: 'user', content: input };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setSending(true);

    try {
      const { data } = await AZURE.post('/governance/finance/planning/chat', {
        message: input,
        history: messages.slice(-10),
        context: context.slice(-5)
      });
      setMessages(prev => [...prev, { role: 'assistant', content: data?.data?.reply || 'Entendi! Processando...' }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: '⚠️ Erro ao processar. Tente novamente.' }]);
    }
    setSending(false);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 flex flex-col h-[600px]">
      {/* Header */}
      <div className="px-4 py-3 border-b flex items-center justify-between" style={{ backgroundColor: '#f0fdf4' }}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: BRAND_GREEN }}>
            <Bot size={16} className="text-white" />
          </div>
          <div>
            <h4 className="font-bold text-gray-900 text-sm">Vukz Fin</h4>
            <p className="text-xs text-gray-500">{t.chatSubtitle(messages.length - 1)}</p>
          </div>
        </div>
        <button onClick={clearHistory} className="text-xs text-gray-400 hover:text-red-500 transition-colors" title="Limpar historico">
          {t.chatClear}
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] px-4 py-2 rounded-xl text-sm whitespace-pre-wrap ${
              msg.role === 'user'
                ? 'bg-green-600 text-white rounded-br-sm'
                : 'bg-gray-100 text-gray-900 rounded-bl-sm'
            }`}>
              {msg.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-gray-100 px-4 py-2 rounded-xl text-sm text-gray-500 animate-pulse">{t.chatThinking}</div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="p-3 border-t flex gap-2">
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
          placeholder={t.chatPlaceholder}
          className="flex-1 border rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-green-500"
          disabled={sending}
        />
        <button onClick={handleSend} disabled={sending || !input.trim()}
          className="px-4 py-2 rounded-lg text-white font-bold" style={{ backgroundColor: BRAND_GREEN }}>
          <Send size={16} />
        </button>
      </div>
    </div>
  );
};

// ============ MAIN PAGE ============
const GovernanceFinancePage = () => {
  const { lang } = useGovernanceLang();
  const t = FINANCE_LABELS[lang] || FINANCE_LABELS.pt;
  const TABS = t.tabs;
  const [activeTab, setActiveTab] = useState('receita');

  return (
    <div>
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
        <div className="flex items-center gap-3">
          <img src="/vukz-logo.png" alt="Vukz" className="w-11 h-11 rounded-xl object-contain bg-gray-50 border border-gray-200 p-0.5" onError={(e) => { e.target.style.display = 'none'; }} />
          <div>
            <h1 className="text-gray-900 text-2xl font-extrabold">{t.title}</h1>
            <p className="text-sm text-gray-500">{t.subtitle}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-4 bg-gray-100 rounded-lg p-1 overflow-x-auto">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold whitespace-nowrap transition-all ${
                activeTab === tab.id ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <tab.icon size={14} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {activeTab === 'receita' && <ReceitaTab t={t} />}
      {activeTab === 'despesas' && (
        <div className="space-y-6">
          <FinancialExpensesTable />
          {/* Breakdown por campanha de marketing (Playbook v1.3 × financial_expenses) */}
          <MarketingCampaignExpenses />
        </div>
      )}
      {activeTab === 'dre' && <FinancialDREDetailedTab />}
      {activeTab === 'fluxo' && <FinancialCashflowDetailedTab />}
      {activeTab === 'produtos' && <ProductProjectionsTab />}
      {activeTab === 'centros' && <CostCentersTab />}
      {activeTab === 'premissas' && <FinancialPremisesTab />}
      {activeTab === 'cenarios' && <FinancialScenariosPage />}
      {activeTab === 'finops' && (
        <div className="space-y-6">
          <AzureSubscriptionsPanel />
          <AzureFXCreditPanel />
          <AzureFinOpsTab />
        </div>
      )}
      {activeTab === 'chat' && <VukzFinChat t={t} />}
    </div>
  );
};

export default GovernanceFinancePage;
